import { useState, useRef, useEffect } from 'react';
import { S } from '@/shared/constants/styles';
import { joinWaitlist } from '@/lib/firebase';
import { isValidEmail } from '@/shared/utils/validators';
import { reportToSentry } from '@/lib/sentry';

const RATE_KEY = 'waitlist:submits';
const WINDOW_MS = 60_000;
const MAX_IN_WINDOW = 3;
const SUCCESS_TTL_MS = 4_000;

function checkRateLimit() {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem(RATE_KEY);
    const log = raw ? JSON.parse(raw) : [];
    const recent = log.filter((t) => now - t < WINDOW_MS);
    return recent.length < MAX_IN_WINDOW;
  } catch {
    return true;
  }
}

function recordSubmit() {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem(RATE_KEY);
    const log = raw ? JSON.parse(raw) : [];
    const recent = log.filter((t) => now - t < WINDOW_MS);
    recent.push(now);
    sessionStorage.setItem(RATE_KEY, JSON.stringify(recent));
  } catch {}
}

export default function WaitlistForm() {
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const successTimer = useRef(null);

  useEffect(() => () => {
    if (successTimer.current) clearTimeout(successTimer.current);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (honeypot) return;

    const clean = email.trim().toLowerCase();
    if (!isValidEmail(clean)) {
      setError('Enter a valid email');
      return;
    }
    if (!checkRateLimit()) {
      setError('Slow down - try again in a moment');
      return;
    }

    setSubmitting(true);
    try {
      await joinWaitlist(clean);
      recordSubmit();
      setSuccess(clean);
      setEmail('');
      if (successTimer.current) clearTimeout(successTimer.current);
      successTimer.current = setTimeout(() => setSuccess(''), SUCCESS_TTL_MS);
    } catch (err) {
      if (err && err.code === 'PERMISSION_DENIED') {
        setError('Already on the list.');
      } else {
        reportToSentry(err, { context: 'WaitlistForm.submit' });
        setError('Could not save - try again');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid #e2e8f0" }}>
      <div style={{ fontSize: 12, color: "#64748b", textAlign: "center", marginBottom: 10, lineHeight: 1.55 }}>
        Want to know when it goes public? Drop your email.
      </div>
      {success && (
        <div style={{
          background: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0",
          borderRadius: 10, padding: "8px 12px", fontSize: 12, marginBottom: 10, textAlign: "center",
        }}>
          Added! Add another below.
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          required
          style={{ ...S.input, marginBottom: 8 }}
        />
        <input
          type="text"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
        />
        {error && (
          <div style={{ fontSize: 12, color: "#991b1b", textAlign: "center", marginBottom: 8 }}>
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          style={{
            ...S.btn(submitting ? "#94a3b8" : "#0f172a"),
            width: "100%", padding: 11, borderRadius: 10, fontSize: 14,
          }}
        >
          {submitting ? "Saving..." : "Notify me"}
        </button>
      </form>
    </div>
  );
}
